/**
 * gen_624.js — Lire l'abscisse fractionnaire / Graduer un segment
 * Cluster 12, Attendu 624 — 9 générateurs
 */

import { shuffle, randInt, randomChoice, makeQcm, makeTexteTrou, makeRelier, makeOrdonner } from '../../../utils';
import { gcd } from './fraction.js';

// ─── SVG builder (shared with gen_623) ────────────────────────

function buildSvg(b, end, points = [], opts = {}) {
  const W = 520, H = 120, ML = 30, MR = 30;
  const usable = W - ML - MR;
  const pxPerUnit = usable / end;
  const toX = (v) => ML + v * pxPerUnit;
  const Y = 60;

  let els = [];
  els.push(`<line x1="${ML}" y1="${Y}" x2="${W - MR - 10}" y2="${Y}" stroke="currentColor" stroke-width="2"/>`);
  els.push(`<polygon points="${W - MR - 10},${Y - 5} ${W - MR},${Y} ${W - MR - 10},${Y + 5}" fill="currentColor"/>`);

  for (let i = 0; i <= end * b; i++) {
    const val = i / b;
    const x = toX(val);
    const isInt = i % b === 0;
    if (isInt) {
      els.push(`<line x1="${x}" y1="50" x2="${x}" y2="70" stroke="currentColor" stroke-width="2"/>`);
      const showLabel = opts.N_only != null
        ? (val === 0 || val === opts.N_only)
        : (opts.showIntLabels !== false);
      if (showLabel) {
        els.push(`<text x="${x}" y="88" font-size="14" font-family="sans-serif" text-anchor="middle" fill="currentColor">${Math.round(val)}</text>`);
      }
    } else {
      els.push(`<line x1="${x}" y1="55" x2="${x}" y2="65" stroke="currentColor" stroke-width="1"/>`);
    }
  }

  // graduation labels (for T8 completer_graduation)
  if (opts.gradLabels) {
    for (const gl of opts.gradLabels) {
      const x = toX(gl.pos);
      const c = gl.color || 'currentColor';
      if (gl.num != null) {
        // fraction EMPILÉE (revue 2026-08-02 : « 1/5 » à plat banni du dessin)
        els.push(`<text x="${x}" y="85" font-size="11" font-family="sans-serif" text-anchor="middle" fill="${c}">${gl.num}</text>`);
        els.push(`<line x1="${x - 7}" y1="88.5" x2="${x + 7}" y2="88.5" stroke="${c}" stroke-width="1.1"/>`);
        els.push(`<text x="${x}" y="99" font-size="11" font-family="sans-serif" text-anchor="middle" fill="${c}">${gl.den}</text>`);
      } else {
        els.push(`<text x="${x}" y="88" font-size="12" font-family="sans-serif" text-anchor="middle" fill="${c}">${gl.text}</text>`);
      }
    }
  }

  for (const p of points) {
    const x = toX(p.pos);
    const c = p.color || '#e53e3e';
    els.push(`<circle cx="${x}" cy="${Y}" r="5" fill="${c}"/>`);
    if (p.label) {
      els.push(`<text x="${x}" y="38" font-size="13" font-weight="bold" fill="${c}" font-family="sans-serif" text-anchor="middle">${p.label}</text>`);
    }
  }

  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}'>${els.join('')}</svg>`;
}

function buildSegmentSvg(L, m) {
  // « L cm » AU-DESSUS du segment (revue 2026-08-02) : les deux étiquettes
  // partageaient la ligne du bas et se chevauchaient quand m ≈ L. Chaque
  // longueur mesurée porte sa FLÈCHE de cote (double pointe gauche-droite,
  // demande utilisateur) : L cm au-dessus (A→B), m cm en dessous (A→C).
  const W = 400, H = 80;
  const pxPerUnit = 340 / L;
  const xA = 30, xB = 30 + L * pxPerUnit, xC = 30 + m * pxPerUnit;
  const cote = (x1, x2, y, color) =>
    `<line x1="${x1 + 4}" y1="${y}" x2="${x2 - 4}" y2="${y}" stroke="${color}" stroke-width="1.2"/>` +
    `<polygon points="${x1 + 1},${y} ${x1 + 7},${y - 3} ${x1 + 7},${y + 3}" fill="${color}"/>` +
    `<polygon points="${x2 - 1},${y} ${x2 - 7},${y - 3} ${x2 - 7},${y + 3}" fill="${color}"/>`;
  let els = [];
  els.push(`<text x="${(xA + xB) / 2}" y="10" font-size="11" font-family="sans-serif" text-anchor="middle" fill="currentColor">${L} cm</text>`);
  els.push(cote(xA, xB, 16, 'currentColor'));
  els.push(`<line x1="${xA}" y1="44" x2="${xB}" y2="44" stroke="currentColor" stroke-width="2"/>`);
  els.push(`<line x1="${xA}" y1="34" x2="${xA}" y2="54" stroke="currentColor" stroke-width="2"/>`);
  els.push(`<line x1="${xB}" y1="34" x2="${xB}" y2="54" stroke="currentColor" stroke-width="2"/>`);
  els.push(`<line x1="${xC}" y1="34" x2="${xC}" y2="54" stroke="#e53e3e" stroke-width="2"/>`);
  els.push(`<text x="${xA}" y="30" font-size="13" font-weight="bold" font-family="sans-serif" text-anchor="middle" fill="currentColor">A</text>`);
  els.push(`<text x="${xB}" y="30" font-size="13" font-weight="bold" font-family="sans-serif" text-anchor="middle" fill="currentColor">B</text>`);
  els.push(`<text x="${xC}" y="30" font-size="13" font-weight="bold" fill="#e53e3e" font-family="sans-serif" text-anchor="middle">C</text>`);
  els.push(cote(xA, xC, 62, '#e53e3e'));
  els.push(`<text x="${(xA + xC) / 2}" y="76" font-size="11" font-family="sans-serif" text-anchor="middle" fill="#e53e3e">${m} cm</text>`);
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}'>${els.join('')}</svg>`;
}

/**
 * Guide-âne : 13 lignes horizontales numérotées 0..12 (nombre FIXE, quel
 * que soit b — sinon le nombre de lignes vendrait la réponse du QCM).
 * Si bPose est fourni : la bande de papier rosée est posée en oblique de
 * la ligne 0 à la ligne bPose, marques aux croisements (vignettes ②-③
 * de la leçon fournie par l'utilisateur, 2026-08-02).
 */
function buildGuideAneSvg(bPose) {
  const W = 420, H = 250, dy = 17, yTop = 20, xL = 40, xR = 410;
  let els = [];
  for (let i = 0; i <= 12; i++) {
    const y = yTop + i * dy;
    els.push(`<line x1="${xL}" y1="${y}" x2="${xR}" y2="${y}" stroke="currentColor" stroke-width="1" opacity="0.45"/>`);
    els.push(`<text x="28" y="${y + 4}" font-size="11" font-family="sans-serif" text-anchor="middle" fill="currentColor">${i}</text>`);
  }
  if (bPose) {
    const x0 = 80, y0 = yTop, x1 = 300, y1 = yTop + bPose * dy;
    els.push(`<line x1="${x0}" y1="${y0}" x2="${x1}" y2="${y1}" stroke="#ec4899" stroke-width="14" stroke-linecap="round" opacity="0.35"/>`);
    els.push(`<line x1="${x0}" y1="${y0}" x2="${x1}" y2="${y1}" stroke="#be185d" stroke-width="1.4"/>`);
    for (let i = 0; i <= bPose; i++) {
      const t = i / bPose;
      els.push(`<circle cx="${x0 + (x1 - x0) * t}" cy="${y0 + (y1 - y0) * t}" r="3" fill="#be185d"/>`);
    }
  }
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}'>${els.join('')}</svg>`;
}

function svgElement(svgStr) {
  const dataUrl = `data:image/svg+xml,${encodeURIComponent(svgStr)}`;
  return { version: 1, elements: [{ type: 'image', config: { imageUrl: dataUrl, maxWidth: 'compact' } }] };
}

const OPT = { niveau: '6eme', matiere: 'maths', attendu: 624 };
function fracStr(a, b) { return `$\\dfrac{${a}}{${b}}$`; }

// ─── T1: QCM quelle est l'abscisse de A ──────────────────────

function lire_abscisse_qcm() {
  let a, b;
  do {
    b = randomChoice([3, 4, 5, 6, 8]);
    a = randInt(1, 3 * b);
  } while (a === b);

  const end = Math.ceil(a / b) + 1;
  const reperes = Array.from({ length: end - 1 }, (_, i) => i + 1); // entiers intermédiaires étiquetés
  const nlConfig = { start: 0, end, step: 1 / b, reperes, points: [{ value: a / b, label: 'A', color: '#fb923c' }] };

  const correct = fracStr(a, b);
  // a = 1 : l'inversion donnerait « b/1 » (dénominateur 1, forme trahie,
  // 8 % des tirages) — remplacée par 1/(b+1), l'erreur « compter les
  // traits, pas les intervalles » (même famille que le relier). Zéro
  // doublon par construction, en chaînes et en valeurs (b ≥ 3) :
  // 1/(b+1) = 2/b ⇒ b = −2 ; 2/b = 1/(b−1) ⇒ b = 2 ; b/a = (a−1)/b ⇒
  // b² = a(a−1), jamais un carré — cf. jetable.
  const d1 = a === 1 ? fracStr(1, b + 1) : fracStr(b, a);
  const offA = a > 1 ? a - 1 : a + 1;
  const d2 = fracStr(offA, b); // off-by-one
  const offB = b > 2 ? b - 1 : b + 1;
  const d3 = fracStr(a, offB); // mauvais dén

  const choix = [correct, d1, d2, d3];
  const texte = `<p>Observe la demi-droite graduée. Quelle est l'abscisse du point A ?</p>{{element:0}}`;

  return {
    ...makeQcm(choix, correct, texte, { ...OPT, label: 'Lire l\'abscisse fractionnaire d\'un point (QCM)' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'lire_abscisse_qcm' }
  };
}

// ─── T2: Saisie écrire l'abscisse ────────────────────────────

function lire_abscisse_trou_saisie() {
  // Ex-champ libre « a/b » (revue 2026-08-02) : 22 % des tirages posaient le
  // point sur un ENTIER (réponse « 10/5 », l'élève tapait 2 → refusé) et
  // 21 % une réponse simplifiable (« 2/3 » pour « 4/6 » → refusé), la
  // tolérance numérique ne lisant pas les fractions. Copremiers au tirage
  // (pool : 3·φ(b) sommé = 93) + fraction à deux cases avec étais
  // (gabarit 622) : l'élève remplit une vraie fraction, comparée en entiers.
  let a, b;
  do {
    b = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10]);
    a = randInt(1, 3 * b);
  } while (gcd(a, b) !== 1);
  const end = Math.ceil(a / b) + 1;
  const reperes = Array.from({ length: end - 1 }, (_, i) => i + 1); // entiers intermédiaires étiquetés
  const nlConfig = { start: 0, end, step: 1 / b, reperes, points: [{ value: a / b, label: 'A', color: '#fb923c' }] };

  const phrase = `$\\text{L'abscisse de A est } \\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}$`;
  // Consigne précisée (2026-08-02) : 2/6 = 1/3 serait mathématiquement
  // juste mais refusé case par case — le dénominateur attendu est ancré
  // sur le geste de lecture (le partage de la droite), unique grâce aux
  // copremiers.
  const texte = `<p>Lis la demi-droite graduée et écris l'abscisse du point A. <b>Le dénominateur est le nombre de parts d'une unité.</b></p>{{element:0}}`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'saisie', strict: true, distracteurs: [], label: 'Écrire l\'abscisse fractionnaire d\'un point' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'lire_abscisse_trou_saisie' }
  };
}

// ─── T3: QCM que vaut chaque graduation ───────────────────────

function lire_pas_qcm() {
  // Espace élargi (enrichissement 2026-07-04) : + dénominateur 9, droite de
  // 2 à 4 unités, borné à nbUnites×b ≤ 36 graduations (= le maximum qui
  // existait déjà : b=12 × 3). L'ORIGINE varie (0 une fois sur deux, sinon
  // 1 à 3) et les entiers intermédiaires sont étiquetés via `reperes` —
  // comme une droite dessinée au tableau.
  const b = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10, 12]);
  const nbUnites = randomChoice([2, 3, 4].filter(e => e * b <= 36));
  const start = randomChoice([0, 0, 1, 2, 3]);
  const end = start + nbUnites;
  const reperes = [];
  for (let v = start + 1; v < end; v++) reperes.push(v);
  // Départ non nul → petit débord à gauche (lineType 'full') : sans lui,
  // l'élève pourrait croire que 1, 2 ou 3 est l'origine (retour user)
  const nlConfig = { start, end, step: 1 / b, points: [], reperes, lineType: start === 0 ? 'half' : 'full' };

  const correct = fracStr(1, b);
  const d1 = b > 2 ? fracStr(1, b - 1) : fracStr(1, b + 2);
  const d2 = fracStr(1, b + 1);
  // Rotation du 4e piège : « nombre de parts » (b) OU « compte TOUTES les
  // graduations de la droite » (1/(nbUnites×b))
  const d3 = randomChoice([`${b}`, fracStr(1, nbUnites * b)]);

  let choix = [...new Set([correct, d1, d2, d3])];
  if (choix.length < 4) choix.push(`${b}`); // collision rare 1/(end×b) = 1/(b+2) → repli sûr
  // « demi-droite » seulement si l'origine 0 est visible (règle user 2026-08-02)
  const support = start === 0 ? 'demi-droite' : 'droite';
  const texte = `<p>Sur cette ${support} graduée, les graduations sont régulières. Que vaut chaque graduation ?</p>{{element:0}}`;

  return {
    ...makeQcm(choix, correct, texte, { ...OPT, label: 'Identifier la valeur d\'une graduation' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'lire_pas_qcm' }
  };
}

// ─── T4: Relier points → abscisses ───────────────────────────

function lire_abscisse_relier() {
  // Refonte bilan prof D3 « plus tard » (arbitré 2026-07-27) : l'ancien 4 ↔ 4
  // tout en /b à valeurs croissantes s'appariait par rang, sans conversion.
  // Gabarit 2 points / 5 cibles, leurres ancrés dans les erreurs documentées
  // (note CSEN fractions/décimaux ; IREM Lyon ; « compter les intervalles,
  // pas les traits ») :
  //   · famille principale tirée au sort et appliquée AUX DEUX points — la
  //     cohérence interne empêche l'élimination par déduction (un « 4/9 »
  //     sans son « 4/3 » se trahirait) et l'élève fautif matérialise SES DEUX
  //     lectures :
  //       traits_intervalles : a/(b+1) — il compte les traits, pas les
  //         intervalles (la droite « en quarts » lue en cinquièmes) ;
  //       inversion : b/a — numérateur et dénominateur inversés (CSEN) ;
  //   · + oubli_entiers sur le point > 1 : (a mod b)/b — il repart de zéro
  //     après le dernier entier étiqueté. Ce leurre peut rester SEUL : l'élève
  //     qui commet cette erreur lit correctement le point < 1.
  // Unicité au produit en croix sur les 5 cibles — collision réelle (point à
  // 7/4 → oubli d'entiers 3/4 = la cible de l'autre point) → RETIRAGE.
  for (let essai = 0; essai < 80; essai++) {
    const b = randomChoice([3, 4, 5, 6]);
    const famille = randomChoice(['traits_intervalles', 'inversion']);
    // A < 1 non entier (a1 ≥ 2 si inversion : b/1 s'écrirait « 4/1 », forme trahie)
    const a1min = famille === 'inversion' ? 2 : 1;
    if (a1min > b - 1) continue;
    const a1 = randInt(a1min, b - 1);
    // B > 1 non entier
    const a2 = randInt(b + 1, 3 * b - 1);
    if (a2 % b === 0) continue;

    const leurres = famille === 'traits_intervalles'
      ? [{ n: a1, d: b + 1, code: famille }, { n: a2, d: b + 1, code: famille }]
      : [{ n: b, d: a1, code: famille }, { n: b, d: a2, code: famille }];
    leurres.push({ n: a2 % b, d: b, code: 'oubli_entiers' });

    const all = [{ n: a1, d: b }, { n: a2, d: b }, ...leurres];
    const collision = all.some((x, i) => all.some((y, j) => j > i && x.n * y.d === y.n * x.d));
    if (collision) continue;

    const end = Math.ceil(a2 / b) + 1;
    const labels = ['A', 'B'];
    const nlPoints = [a1, a2].map((a, i) => ({ value: a / b, label: labels[i], color: '#fb923c' }));
    const reperes = Array.from({ length: end - 1 }, (_, i) => i + 1); // entiers intermédiaires étiquetés
    const nlConfig = { start: 0, end, step: 1 / b, reperes, points: nlPoints };

    const pairs = [a1, a2].map((a, i) => ({ source: labels[i], target: fracStr(a, b) }));
    const ciblesLeurres = leurres.map(l => ({ target: fracStr(l.n, l.d), code: l.code }));
    const texte = `<p>Relie chaque point de la demi-droite à son abscisse. Attention : certaines fractions ne correspondent à aucun point.</p>{{element:0}}`;

    const payload = {
      ...makeRelier(pairs, texte, {
        ...OPT, label: 'Relier points et abscisses fractionnaires',
        cibles_leurres: ciblesLeurres,
      }),
      elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
      _meta: { generatorKey: 'lire_abscisse_relier' }
    };
    payload._meta.params = {
      b, points: [a1, a2], famille,
      distracteurs: Object.fromEntries(ciblesLeurres.map(l => [l.target, l.code])),
    };
    return payload;
  }
  // Repli déterministe vérifié : b = 4, A = 3/4, B = 9/4, famille traits →
  // cibles 3/4, 9/4 ; leurres 3/5, 9/5, 1/4 — toutes distinctes.
  const nlConfig = {
    start: 0, end: 4, step: 1 / 4, reperes: [1, 2, 3],
    points: [{ value: 3 / 4, label: 'A', color: '#fb923c' }, { value: 9 / 4, label: 'B', color: '#fb923c' }],
  };
  const payload = {
    ...makeRelier(
      [{ source: 'A', target: fracStr(3, 4) }, { source: 'B', target: fracStr(9, 4) }],
      `<p>Relie chaque point de la demi-droite à son abscisse. Attention : certaines fractions ne correspondent à aucun point.</p>{{element:0}}`,
      {
        ...OPT, label: 'Relier points et abscisses fractionnaires',
        cibles_leurres: [
          { target: fracStr(3, 5), code: 'traits_intervalles' },
          { target: fracStr(9, 5), code: 'traits_intervalles' },
          { target: fracStr(1, 4), code: 'oubli_entiers' },
        ],
      }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'lire_abscisse_relier' }
  };
  return payload;
}

// ─── T5: V/F l'abscisse de A est … ───────────────────────────

function lire_abscisse_vf() {
  let a, b;
  do {
    b = randomChoice([3, 4, 5, 6]);
    a = randInt(1, 2 * b);
  } while (a === b);

  const end = Math.ceil(a / b) + 1;
  const reperes = Array.from({ length: end - 1 }, (_, i) => i + 1); // entiers intermédiaires étiquetés
  const nlConfig = { start: 0, end, step: 1 / b, reperes, points: [{ value: a / b, label: 'A', color: '#fb923c' }] };

  const offA = a < 2 * b ? a + 1 : a - 1;
  const offB = b > 2 ? b - 1 : b + 1;

  const lines = [
    `L'abscisse de A est ${fracStr(a, b)}`,
    `L'abscisse de A est ${fracStr(offA, b)}`,
    `L'abscisse de A est ${fracStr(a, offB)}`
  ];
  const answers = [0, 1, 1]; // V, F, F

  const idx = shuffle([0, 1, 2]);
  const texte = `<p>Observe la demi-droite graduée puis dis si chaque affirmation est vraie ou fausse.</p>{{element:0}}`;

  const orderedLines = idx.map(i => lines[i]);
  const orderedAnswers = idx.map(i => answers[i]);
  const colonnes = ['Vrai', 'Faux'];
  const br = orderedLines.map((l, i) => `[${l}|${colonnes[orderedAnswers[i]]}]`).join('');

  return {
    type: 'qcm',
    texte,
    contenu: { choix: [], bonne_reponse: br },
    elements_additionnels: {
      version: 1,
      elements: [
        { type: 'numberline', config: nlConfig },
        { type: 'table', config: { type: 'vraiFaux', colonnes, lignes: orderedLines, bonnesReponses: orderedAnswers } }
      ]
    },
    niveau: '6eme', matiere: 'maths', attendu: 624,
    _meta: { generatorKey: 'lire_abscisse_vf' }
  };
}

// ─── T6: Texte à trous drag (méthode de lecture) ──────────────

function lire_methode_drag() {
  const b = randomChoice([3, 4, 5, 6, 7, 8, 9, 10]);
  const a = randInt(1, b - 1); // fraction < 1
  const nlConfig = { start: 0, end: 1, step: 1 / b, points: [{ value: a / b, label: 'A', color: '#fb923c' }] };

  // Étais \rule (revue 2026-08-02, même fix que 620/621/623) : sans eux la
  // barre de fraction disparaît sous les champs de saisie
  const phrase = `$\\text{Entre 0 et 1, il y a } {{${b}}} \\text{ intervalles égaux.}$\n$\\text{Chaque intervalle vaut } \\dfrac{{{1}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}.$\n$\\text{Le point A est à la } {{${a}}} \\text{-ième graduation, donc son abscisse est } \\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}.$`;

  // distracteurs
  const candidates = [b - 1, b + 1, a + 1, a > 1 ? a - 1 : a + 2]
    .map(String)
    .filter(v => v !== String(b) && v !== '1' && v !== String(a));
  const dist = shuffle([...new Set(candidates)]).slice(0, 2);

  const texte = `<p>Complète la méthode pour lire l'abscisse du point A.</p>{{element:0}}`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'drag', distracteurs: dist, label: 'Méthode de lecture d\'une abscisse (drag)' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'lire_methode_drag' }
  };
}

// ─── T7: Saisie fraction d'un segment ─────────────────────────

function fraction_segment_saisie() {
  // Copremiers (revue 2026-08-02) : 42 % de réponses simplifiables
  // (« 6/12 » stocké, « 1/2 » tapé → refusé). Longueurs 7/9/11 ajoutées
  // pour le pool : somme des φ(L) = 42 variantes.
  const L = randomChoice([4, 5, 6, 7, 8, 9, 10, 11, 12]);
  let m;
  do { m = randInt(1, L - 1); } while (gcd(m, L) !== 1);
  const svg = buildSegmentSvg(L, m);

  // Fraction à deux cases avec étais (gabarit 622, comme T2) à la place du
  // champ libre « a/b » (audit fractions en texte 2026-09-06) : l'élève
  // remplit une vraie fraction verticale, plus de barre « / » à taper ni à
  // lire. Copremiers au tirage : une seule écriture attendue, case par case.
  const texte = `<p>Le segment [AB] mesure ${L} cm. Le point C est placé tel que AC = ${m} cm. Quelle fraction de AB représente AC ?</p>{{element:0}}`;
  const phrase = `$\\text{AC représente } \\dfrac{{{${m}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${L}}}} \\text{ de AB.}$`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'saisie', strict: true, distracteurs: [], label: 'Fraction d\'un segment (saisie)' }),
    elements_additionnels: svgElement(svg),
    _meta: { generatorKey: 'fraction_segment_saisie' }
  };
}

// ─── T8: Texte à trous saisie (compléter graduation) ─────────

function completer_graduation_saisie() {
  const b = randomChoice([3, 4, 5, 6, 8]);

  // choose 2 positions to mask
  const positions = [];
  for (let i = 1; i < b; i++) positions.push(i);
  const masked = shuffle(positions).slice(0, 2).sort((x, y) => x - y);

  // build graduation labels for SVG
  const gradLabels = [];
  gradLabels.push({ pos: 0, text: '0', color: 'currentColor' });
  for (let i = 1; i < b; i++) {
    if (masked.includes(i)) {
      gradLabels.push({ pos: i / b, text: '?', color: '#e53e3e' });
    } else {
      gradLabels.push({ pos: i / b, num: i, den: b, color: 'currentColor' });
    }
  }
  gradLabels.push({ pos: 1, text: '1', color: 'currentColor' });

  const svg = buildSvg(b, 1, [], { showIntLabels: false, gradLabels });

  // Phrase en UNE ligne KaTeX : les graduations connues en fractions
  // verticales, les manquantes en fractions à deux cases avec étais
  // (gabarit 622). L'élève tapait « 2/4 » dans un trou de texte (audit
  // fractions en texte 2026-09-06) ; il remplit désormais numérateur et
  // dénominateur, le dénominateur étant le nombre de parts de l'unité.
  const parts = ['0'];
  for (let i = 1; i < b; i++) {
    if (masked.includes(i)) {
      parts.push(`\\dfrac{{{${i}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}`);
    } else {
      parts.push(`\\dfrac{${i}}{${b}}`);
    }
  }
  parts.push('1');
  const phrase = `$\\text{Les graduations sont : } ${parts.join(' ;\\; ')}.$`;

  const texte = `<p>Sur cette demi-droite graduée, l'unité est divisée en ${b} parts égales. Complète les graduations manquantes. <b>Le dénominateur est le nombre de parts d'une unité.</b></p>{{element:0}}`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'saisie', distracteurs: [], label: 'Compléter les graduations manquantes (saisie)' }),
    elements_additionnels: svgElement(svg),
    _meta: { generatorKey: 'completer_graduation_saisie' }
  };
}

/* ──────────────────────────────────────────────────────────────
 * SUPPRIMÉ 2026-08-02 (lire_sans_unite_qcm) : « quelle est l'abscisse
 * de A » sans entiers étiquetés — doublonnait lire_abscisse_qcm (même
 * question) et lire_pas_qcm (déduire le pas), alors que l'attendu
 * officiel « Graduer un segment de longueur donnée » manquait de
 * formats segment. Remplacé par graduer_segment_ordonner.
 * ────────────────────────────────────────────────────────────── */

// ─── T9: Ordonner la méthode du guide-âne ────────────────────

function graduer_segment_ordonner() {
  // « Graduer un segment de longueur donnée » : la méthode du guide-âne
  // (leçon fournie par l'utilisateur, 2026-08-02) en 4 étapes à remettre
  // dans l'ordre. b hors {2, 4, 8} — demis/quarts/huitièmes se font par
  // pliage (note de la leçon). Étape « je marque » : b − 1 graduations
  // pour b parts — l'erreur traits/intervalles travaillée au passage.
  // Pas de longueur en cm (retour user) : le guide-âne sert justement à
  // graduer un segment de longueur QUELCONQUE — annoncer 6 cm en tiers
  // trahirait la méthode (on mesurerait tous les 2 cm à la règle).
  const NOMS = { 3: 'tiers', 5: 'cinquièmes', 6: 'sixièmes', 7: 'septièmes', 9: 'neuvièmes', 10: 'dixièmes' };
  const b = randomChoice([3, 5, 6, 7, 9, 10]);
  const nom = randomChoice(['[AB]', '[CD]', '[EF]', '[GH]', '[MN]', '[RS]', '[TU]', '[UV]']);

  const etapes = [
    'Je reporte la longueur du segment sur une bande de papier.',
    `Je positionne une extrémité du report sur la ligne 0 et l'autre sur la ligne ${b} du guide-âne.`,
    `Je marque les ${b - 1} graduations pour obtenir ${b} parts égales.`,
    'Je reporte les graduations sur le segment.',
  ];
  const texte = `<p>Pour graduer le segment ${nom} en ${NOMS[b]} avec un guide-âne, remets les étapes de la méthode dans l'ordre.</p>{{element:0}}`;

  return {
    ...makeOrdonner(etapes, texte, { ...OPT, label: 'Ordonner la méthode du guide-âne' }),
    elements_additionnels: svgElement(buildGuideAneSvg(b)),
    _meta: { generatorKey: 'graduer_segment_ordonner' }
  };
}

// ─── T10: QCM les lignes du guide-âne ────────────────────────

function graduer_segment_qcm() {
  // Le cœur mathématique de la méthode : les extrémités sur les lignes
  // 0 et b pour b parts. Guide-âne NU (la bande posée vendrait la
  // réponse). Distracteurs nommés : 0 et b−1 (intervalles/traits),
  // 1 et b (départ décalé), 0 et 2b (le double ; b+1 si 2b > 12).
  // Distincts par construction pour tout b ≥ 3.
  // Segment de longueur QUELCONQUE (retour user) : c'est la raison d'être
  // du guide-âne — nom de segment variable, jamais de mesure en cm.
  const NOMS = { 3: 'tiers', 5: 'cinquièmes', 6: 'sixièmes', 7: 'septièmes', 9: 'neuvièmes', 10: 'dixièmes' };
  const b = randomChoice([3, 5, 6, 7, 9, 10]);
  const nom = randomChoice(['[AB]', '[CD]', '[EF]', '[GH]', '[MN]', '[RS]', '[TU]', '[UV]']);

  const correct = `les lignes 0 et ${b}`;
  const dDouble = 2 * b <= 12 ? `les lignes 0 et ${2 * b}` : `les lignes 0 et ${b + 1}`;
  const choix = [correct, `les lignes 0 et ${b - 1}`, `les lignes 1 et ${b}`, dDouble];
  const texte = `<p>Je veux partager le segment ${nom} en ${NOMS[b]} avec ce guide-âne. Je reporte sa longueur sur une bande de papier. Sur quelles lignes dois-je placer les deux extrémités du report ?</p>{{element:0}}`;

  return {
    ...makeQcm(choix, correct, texte, { ...OPT, label: 'Choisir les lignes du guide-âne' }),
    elements_additionnels: svgElement(buildGuideAneSvg()),
    _meta: { generatorKey: 'graduer_segment_qcm' }
  };
}

// ─── Export ───────────────────────────────────────────────────

const GENERATORS = {
  lire_abscisse_qcm,
  lire_abscisse_trou_saisie,
  lire_pas_qcm,
  lire_abscisse_relier,
  lire_abscisse_vf,
  lire_methode_drag,
  fraction_segment_saisie,
  completer_graduation_saisie,
  graduer_segment_ordonner,
  graduer_segment_qcm
};

const FORMAT_LABELS = {
    qcm: 'QCM', saisie: 'Saisie', relier: 'Relier',
    vf: 'Vrai/Faux', drag: 'Drag', trou_saisie: 'Saisie trous',
    ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire_abscisse', label: 'Lire l\'abscisse fractionnaire d\'un point', formatsList: buildFormats('lire_abscisse') },
    { id: 'lire_pas', label: 'Identifier la valeur d\'une graduation', formatsList: buildFormats('lire_pas') },
    { id: 'lire_methode', label: 'Méthode de lecture d\'une abscisse', formatsList: buildFormats('lire_methode') },
    { id: 'fraction_segment', label: 'Fraction d\'un segment', formatsList: buildFormats('fraction_segment') },
    { id: 'completer_graduation', label: 'Compléter les graduations manquantes', formatsList: buildFormats('completer_graduation') },
    { id: 'graduer_segment', label: 'Graduer un segment de longueur donnée', formatsList: buildFormats('graduer_segment') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : lire une abscisse se fait en comptant les
    // graduations (même arbitrage que 620-623, revue 2026-08-02)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};
